import api from './api';

export function isStandaloneApp() {
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function getPushSubscription() {
  if (!isPushSupported()) return null;

  const registration = await getServiceWorkerRegistration();
  if (!registration) return null;

  return registration.pushManager.getSubscription();
}

export async function subscribeToPushNotifications() {
  if (!isPushSupported()) {
    throw new Error('Browser push notifications are not supported on this device.');
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    throw new Error('Installable app features are available after creating a production build.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  const existingSubscription = await registration.pushManager.getSubscription();

  if (existingSubscription) {
    await saveSubscription(existingSubscription);
    return existingSubscription;
  }

  const publicKey = await getVapidPublicKey();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  await saveSubscription(subscription);
  return subscription;
}

export async function unsubscribeFromPushNotifications() {
  const subscription = await getPushSubscription();
  if (!subscription) return false;

  try {
    await api.post('/push/unregister', { endpoint: subscription.endpoint });
  } finally {
    await subscription.unsubscribe();
  }

  return true;
}

async function getVapidPublicKey() {
  const response = await api.get('/push/vapid-public-key');
  const publicKey = response.data?.publicKey;

  if (!publicKey) {
    throw new Error('VAPID public key is not configured on the server.');
  }

  return publicKey;
}

async function saveSubscription(subscription) {
  const serialized = typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription;
  await api.post('/push/register', {
    subscription: {
      ...serialized,
      platform: 'web',
    },
  });
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

async function getServiceWorkerRegistration() {
  const registrations = await navigator.serviceWorker.getRegistrations();
  const existingRegistration = registrations.find((registration) => (
    registration.active || registration.waiting || registration.installing
  ));

  if (!existingRegistration && process.env.NODE_ENV !== 'production') {
    return null;
  }

  return navigator.serviceWorker.ready;
}
